import { useAgentModelCapabilities } from '@/hooks/useAgentModelCapabilities';
import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { agentApi } from '@/api/agent.api';
import { fullErrorText } from '@/api/errors';
import { applicationApi } from '@/api/application.api';
import {
  AgentSchema,
  AgentModelConfigSchema,
  AgentModelExtraConfigSchema,
  AgentChannelConfigSchema,
  AgentPluginConfigSchema,
  AgentAdvancedConfigSchema,
  CompressionConfigSchema,
  FilesystemConfigSchema,
  TodoListConfigSchema,
  AskUserConfigSchema,
  ReadBeforeWriteConfigSchema,
  WebSearchConfigSchema,
  CurrentTimeConfigSchema,
} from '@/proto/agentplatform/v1/agent_pb';
import {
  ChannelType,
  ChannelAccountBindingMode,
  ToolPermissionsSchema,
  ToolPermissionRuleSchema,
  ToolPermissionDefaultAction,
} from '@/proto/agentplatform/v1/channel_pb';
import { ParamSchema } from '@/proto/agentplatform/v1/param_pb';
import { sandboxApi } from '@/api/sandbox.api';
import { sandboxClassApi } from '@/api/sandboxClass.api';
import { cortexApi, memoryApi, memoryClassApi } from '@/api/cortex.api';
import { ragPluginApi } from '@/api/ragPlugin.api';
import { mcpApi } from '@/api/mcp.api';
import { mcpClassApi } from '@/api/mcpClass.api';
import {
  PLUGIN_TYPE_SANDBOX,
  PLUGIN_TYPE_SKILLGROUP,
  PLUGIN_TYPE_RAG,
  PLUGIN_TYPE_MEMORY,
  PLUGIN_TYPE_MCP,
  PLUGIN_TYPE_TOOLKIT,
  PLUGIN_TYPE_USER_TOOLKIT,
  PLUGIN_TYPE_APPLICATION,
  PLUGIN_SUB_TYPE_CLASS,
  toBackendPluginType,
} from '@/constants/plugin';
import { type ParamItem } from '@/components/ParamConfigEditor';
import { generateRandomSuffix } from '@/utils/utils';
import AgentForm from '@/components/Agents/AgentForm';
import type { FormState, AgentFormHandlers } from '@/components/Agents/agentFormTypes';
import {
  CHANNEL_PARAM_DEFINITIONS,
  DEFAULT_FAULT_TOLERANCE,
  FILESYSTEM_ALL_TOOLS,
} from '@/components/Agents/agentFormTypes';
import {
  buildFaultToleranceConfig,
  validateFaultTolerance,
} from '@/components/Agents/faultTolerance';
import {
  DEFAULT_AI_APPROVAL,
  buildToolPreHooks,
  validateToolPreHooks,
} from '@/components/Agents/toolPreHooks';

function buildParams(params: ParamItem[]) {
  return params
    .filter((p) => p.name)
    .map((p) => {
      const caseField =
        p.valueType === 'randomType'
          ? 'randomType'
          : p.valueType === 'reference'
            ? 'reference'
            : p.valueType === 'schema'
              ? 'schema'
              : 'value';
      return create(ParamSchema, {
        type: p.type,
        name: p.name,
        target: p.target,
        configurable: p.configurable,
        paramValue: {
          case: caseField,
          value: p.value,
        },
      });
    });
}

export default function CreateAgentPage() {
  const navigate = useNavigate();
  const [showApiKey, setShowApiKey] = useState(false);
  const [form, setForm] = useState<FormState>({
    name: '',
    description: '',
    model_name: '',
    api_key: '',
    api_key_ref: '',
    system_prompt: '',
    system_prompt_configurable: false,
    model_params: [],
    extraConfig: {
      temperature: 0.7,
      enableThinking: undefined,
      interleavedThinking: undefined,
      reasoningEffort: '',
      presencePenalty: 0,
      frequencyPenalty: 0,
      maxOutputLength: BigInt(0),
      contextLength: BigInt(0),
    },
    enableFilesystem: true,
    filesystemTools: [...FILESYSTEM_ALL_TOOLS],
    todoListEnable: false,
    todoListEnableDesc: false,
    askUserEnable: false,
    readBeforeWriteEnable: false,
    webSearchEnable: false,
    webSearchApiKey: '',
    webSearchMaxResults: 0,
    currentTimeEnable: true,
    compression: {
      enabled: false,
    },
    toolPermissions: {
      preHooks: [],
      aiApproval: { ...DEFAULT_AI_APPROVAL },
      allow: [],
      deny: [],
      defaultAction: ToolPermissionDefaultAction.UNSPECIFIED,
      enableDesc: false,
      descModel: '',
    },
    faultTolerance: structuredClone(DEFAULT_FAULT_TOLERANCE),
    channelBindingMode: ChannelAccountBindingMode.TEMPLATE,
    channels: [],
    plugins: [],
  });

  const createMutation = useMutation({
    mutationFn: agentApi.createAgent,
    onSuccess: () => navigate('/agents'),
    onError: (error) => toast.error(`创建失败：${fullErrorText(error)}`),
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({
      ...prev,
      [key]: value,
      ...(key === 'model_name' && value !== prev.model_name
        ? {
            extraConfig: {
              ...prev.extraConfig,
              enableThinking: undefined,
              interleavedThinking: undefined,
            },
          }
        : {}),
    }));

  const addPlugin: AgentFormHandlers['addPlugin'] = (defaults) => {
    setForm((prev) => ({
      ...prev,
      plugins: [
        ...prev.plugins,
        {
          plugin_type: defaults?.plugin_type ?? PLUGIN_TYPE_SANDBOX,
          plugin_sub_type: defaults?.plugin_sub_type ?? PLUGIN_SUB_TYPE_CLASS,
          plugin_id: '',
          plugin_params: [],
          memoryMode: 'mem0',
          isNew: true,
        },
      ],
    }));
  };

  const removePlugin: AgentFormHandlers['removePlugin'] = (index) => {
    setForm((prev) => ({
      ...prev,
      plugins: prev.plugins.filter((_, i) => i !== index),
    }));
  };

  const updatePlugin: AgentFormHandlers['updatePlugin'] = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.plugins];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, plugins: updated };
    });
  };

  const updatePluginParam: AgentFormHandlers['updatePluginParam'] = (
    pluginIndex,
    paramIndex,
    field,
    value
  ) => {
    setForm((prev) => {
      const plugin = prev.plugins[pluginIndex];
      const updated = [...plugin.plugin_params];
      updated[paramIndex] = { ...updated[paramIndex], [field]: value };
      const plugins = [...prev.plugins];
      plugins[pluginIndex] = { ...plugin, plugin_params: updated };
      return { ...prev, plugins };
    });
  };

  const fetchPluginInfo: AgentFormHandlers['fetchPluginInfo'] = async (
    pluginIndex,
    pluginType,
    uid,
    subType
  ) => {
    if (!uid) return;
    try {
      if (pluginType === PLUGIN_TYPE_SANDBOX && subType === PLUGIN_SUB_TYPE_CLASS) {
        const data = await sandboxClassApi.getSandboxClass(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: data.template?.type || '',
            provider: '',
          });
          updatePlugin(
            pluginIndex,
            'plugin_params',
            (data.params || []).map((p) => ({
              type: p.type || 'string',
              name: p.name || '',
              target: p.name || '',
              value: p.paramValue?.value || '',
              valueType: p.paramValue?.case || 'value',
              configurable: p.configurable,
            }))
          );
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_SANDBOX) {
        const data = await sandboxApi.getSandbox(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: data.type || '',
            provider: data.provider || '',
          });
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_SKILLGROUP && subType === PLUGIN_SUB_TYPE_CLASS) {
        const data = await cortexApi.getSkillGroupClass(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: data.template?.provider || '',
          });
          updatePlugin(
            pluginIndex,
            'plugin_params',
            (data.params || []).map((p) => ({
              type: p.type || 'string',
              name: p.name || '',
              target: p.name || '',
              value: p.paramValue?.value || '',
              valueType: p.paramValue?.case || 'value',
              configurable: p.configurable,
            }))
          );
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_SKILLGROUP) {
        const data = await cortexApi.getSkillGroup(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: data.provider || '',
          });
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_RAG && subType === PLUGIN_SUB_TYPE_CLASS) {
        const data = await ragPluginApi.getRAGClass(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: '',
          });
          updatePlugin(
            pluginIndex,
            'plugin_params',
            (data.params || []).map((p) => ({
              type: p.type || 'string',
              name: p.name || '',
              target: p.target || p.name || '',
              value: p.paramValue?.value || '',
              valueType: p.paramValue?.case || 'value',
              configurable: p.configurable,
            }))
          );
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_MEMORY && subType === PLUGIN_SUB_TYPE_CLASS) {
        const data = await memoryClassApi.getMemoryClass(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: data.template?.provider || '',
          });
          updatePlugin(
            pluginIndex,
            'memoryInteractionMode',
            data.template?.interactionMode || 'AUTO_EXTRACT_AND_TOOL'
          );
          updatePlugin(
            pluginIndex,
            'plugin_params',
            (data.params || []).map((p) => ({
              type: p.type || 'string',
              name: p.name || '',
              target: p.name || '',
              value: p.paramValue?.value || '',
              valueType: p.paramValue?.case || 'value',
              configurable: p.configurable,
            }))
          );
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_RAG) {
        const data = await ragPluginApi.getRAG(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: '',
          });
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_MEMORY) {
        const data = await memoryApi.getMemory(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: data.provider || '',
          });
          updatePlugin(
            pluginIndex,
            'memoryInteractionMode',
            data.interactionMode || 'AUTO_EXTRACT_AND_TOOL'
          );
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
      }
      if (pluginType === PLUGIN_TYPE_MCP && subType === PLUGIN_SUB_TYPE_CLASS) {
        const data = await mcpClassApi.getMCPClass(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: '',
          });
          updatePlugin(
            pluginIndex,
            'plugin_params',
            (data.params || []).map((p) => ({
              type: p.type || 'string',
              name: p.name || '',
              target: p.name || '',
              value: p.paramValue?.case === 'value' ? p.paramValue.value : '',
              valueType: p.paramValue?.case || 'value',
              configurable: p.configurable,
            }))
          );
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_MCP) {
        const data = await mcpApi.getMCP(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: '',
          });
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
        return;
      }
      if (pluginType === PLUGIN_TYPE_TOOLKIT) {
        const data = await agentApi.listInternalToolkits();
        const tk = data?.data?.find((t) => t.name === uid);
        if (tk) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: tk.name || '',
            description: tk.description || '',
            type: '',
            provider: '',
          });
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
      }
      if (pluginType === PLUGIN_TYPE_APPLICATION) {
        const data = await applicationApi.get(uid);
        if (data) {
          updatePlugin(pluginIndex, 'selectedItemData', {
            name: data.name || '',
            description: data.description || '',
            type: '',
            provider: '',
          });
          updatePlugin(pluginIndex, 'plugin_params', []);
        }
      }
    } catch (err) {
      toast.error(`获取插件信息失败：${fullErrorText(err)}`);
    }
  };

  const addChannel: AgentFormHandlers['addChannel'] = () => {
    setForm((prev) => ({
      ...prev,
      channels: [
        ...prev.channels,
        {
          channelType: ChannelType.FEISHU,
          bindingMode: prev.channelBindingMode,
          channelParamValues: {},
          isNew: true,
        },
      ],
    }));
  };

  const removeChannel: AgentFormHandlers['removeChannel'] = (index) => {
    setForm((prev) => ({
      ...prev,
      channels: prev.channels.filter((_, i) => i !== index),
    }));
  };

  const updateChannel: AgentFormHandlers['updateChannel'] = (index, field, value) => {
    setForm((prev) => {
      const updated = [...prev.channels];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, channels: updated };
    });
  };

  const getDuplicateParamNames = (): Set<string> => {
    const seen = new Set<string>();
    const duplicates = new Set<string>();
    for (const plugin of form.plugins) {
      for (const p of plugin.plugin_params) {
        if (p.name) {
          if (seen.has(p.name)) {
            duplicates.add(p.name);
          } else {
            seen.add(p.name);
          }
        }
      }
    }
    return duplicates;
  };

  const capabilities = useAgentModelCapabilities(form);

  const handleSubmit = () => {
    if (capabilities.error) {
      toast.error(capabilities.error);
      return;
    }
    const extraConfig = capabilities.extraConfig;
    const ftError = validateFaultTolerance(form.faultTolerance);
    if (ftError) {
      toast.error(ftError);
      return;
    }
    const preHookError = validateToolPreHooks(
      form.toolPermissions.preHooks,
      form.toolPermissions.aiApproval
    );
    if (preHookError) {
      toast.error(preHookError);
      return;
    }

    const systemPromptParam: ParamItem[] = form.system_prompt_configurable
      ? [
          {
            type: 'string',
            name: 'system_prompt',
            target: 'model_config.system_prompt',
            value: form.system_prompt,
            valueType: 'value',
            configurable: true,
          },
        ]
      : [];

    const modelConfig = create(AgentModelConfigSchema, {
      modelName: form.model_name,
      apiKeyRef: form.api_key_ref,
      systemPrompt: form.system_prompt,
      extraConfig: create(AgentModelExtraConfigSchema, {
        temperature: form.extraConfig.temperature,
        maxTokens: Number(extraConfig.maxOutputLength),
        maxContextLength: Number(extraConfig.contextLength),
        enableThinking: extraConfig.enableThinking,
        reasoningEffort: form.extraConfig.reasoningEffort.trim() || undefined,
        presencePenalty: form.extraConfig.presencePenalty,
        frequencyPenalty: form.extraConfig.frequencyPenalty,
      }),
      modelParams: buildParams([...form.model_params, ...systemPromptParam]),
    });

    const channelConfigs = form.channels
      .filter((c) => c.channelType !== ChannelType.CHANNEL_TYPE_UNSPECIFIED)
      .map((c) => {
        const channelParams = (CHANNEL_PARAM_DEFINITIONS[c.channelType] ?? []).map((def) =>
          create(ParamSchema, {
            type: def.type,
            name: def.name,
            target: def.target,
            configurable: c.bindingMode === ChannelAccountBindingMode.INSTANCE,
            paramValue: {
              case: 'value' as const,
              value:
                c.bindingMode === ChannelAccountBindingMode.TEMPLATE
                  ? (c.channelParamValues[def.name] ?? def.defaultValue)
                  : def.defaultValue,
            },
          })
        );
        return create(AgentChannelConfigSchema, {
          channelType: c.channelType,
          bindingMode: c.bindingMode,
          channelParams,
        });
      });

    const pluginConfigs = form.plugins
      .map((p) => {
        if (!p.plugin_id && p.plugin_type !== PLUGIN_TYPE_USER_TOOLKIT) {
          return undefined;
        }
        if (p.plugin_type === PLUGIN_TYPE_USER_TOOLKIT) {
          return create(AgentPluginConfigSchema, {
            name: generateRandomSuffix(),
            pluginType: toBackendPluginType(p.plugin_type, p.plugin_sub_type),
            pluginUid: '',
            pluginParams: buildParams(p.plugin_params),
          });
        }
        const params = buildParams(p.plugin_params);
        if (p.plugin_type === PLUGIN_TYPE_MEMORY) {
          params.push(
            create(ParamSchema, {
              name: 'interaction_mode',
              paramValue: {
                case: 'value',
                value: p.memoryInteractionMode || 'AUTO_EXTRACT_AND_TOOL',
              },
            })
          );
        }
        return create(AgentPluginConfigSchema, {
          name: `${p.selectedItemData?.name || p.plugin_id || ''}-${generateRandomSuffix()}`,
          pluginType: toBackendPluginType(p.plugin_type, p.plugin_sub_type, p.memoryMode),
          pluginUid: p.plugin_id,
          pluginParams: params,
        });
      })
      .filter((p) => p !== undefined);

    const microAppIndex = form.plugins.findIndex((p) => p.isMicroApp);
    const sandboxMicroAppName =
      microAppIndex !== -1 ? (pluginConfigs[microAppIndex]?.name ?? '') : '';

    const hasRules =
      form.toolPermissions.preHooks.length > 0 ||
      form.toolPermissions.aiApproval.enabled ||
      form.toolPermissions.allow.length > 0 ||
      form.toolPermissions.deny.length > 0;
    const hasDefaultAction =
      form.toolPermissions.defaultAction !== ToolPermissionDefaultAction.UNSPECIFIED;
    const hasToolPermissions =
      hasRules ||
      hasDefaultAction ||
      form.toolPermissions.enableDesc ||
      !!form.toolPermissions.descModel.trim();
    const advancedConfig =
      form.compression.enabled ||
      hasToolPermissions ||
      !!sandboxMicroAppName ||
      form.enableFilesystem ||
      form.todoListEnable ||
      form.faultTolerance.enabled ||
      form.askUserEnable ||
      form.readBeforeWriteEnable ||
      form.webSearchEnable ||
      form.currentTimeEnable
        ? create(AgentAdvancedConfigSchema, {
            ...(form.compression.enabled
              ? {
                  compression: create(CompressionConfigSchema, {
                    enabled: form.compression.enabled,
                  }),
                }
              : {}),
            ...(hasToolPermissions
              ? {
                  toolPermissions: create(ToolPermissionsSchema, {
                    allow: form.toolPermissions.allow.map((r) =>
                      create(ToolPermissionRuleSchema, {
                        toolName: r.toolName,
                        argument: r.argument,
                        pattern: r.pattern,
                      })
                    ),
                    deny: form.toolPermissions.deny.map((r) =>
                      create(ToolPermissionRuleSchema, {
                        toolName: r.toolName,
                        argument: r.argument,
                        pattern: r.pattern,
                      })
                    ),
                    defaultAction: hasDefaultAction
                      ? form.toolPermissions.defaultAction
                      : ToolPermissionDefaultAction.REQUIRE_HUMAN,
                    enableDesc: form.toolPermissions.enableDesc,
                    descModel: form.toolPermissions.descModel.trim(),
                  }),
                }
              : {}),
            ...(sandboxMicroAppName ? { sandboxMicroApp: sandboxMicroAppName } : {}),
            ...(form.enableFilesystem
              ? {
                  filesystem: create(FilesystemConfigSchema, {
                    enable: true,
                    tools: form.filesystemTools,
                  }),
                }
              : {}),
            ...(form.todoListEnable
              ? {
                  todoList: create(TodoListConfigSchema, {
                    enable: true,
                    enableDesc: form.todoListEnableDesc,
                  }),
                }
              : {}),
            ...(form.askUserEnable
              ? {
                  askUser: create(AskUserConfigSchema, {
                    enable: true,
                  }),
                }
              : {}),
            ...(form.readBeforeWriteEnable
              ? {
                  readBeforeWrite: create(ReadBeforeWriteConfigSchema, {
                    enable: true,
                  }),
                }
              : {}),
            ...(form.webSearchEnable
              ? {
                  webSearch: create(WebSearchConfigSchema, {
                    enable: true,
                    tavilyApiKey: form.webSearchApiKey.trim(),
                    maxResults: form.webSearchMaxResults > 0 ? form.webSearchMaxResults : 0,
                  }),
                }
              : {}),
            ...(form.currentTimeEnable
              ? {
                  currentTime: create(CurrentTimeConfigSchema, {
                    enable: true,
                  }),
                }
              : {}),
            ...(form.faultTolerance.enabled
              ? { faultTolerance: buildFaultToleranceConfig(form.faultTolerance) }
              : {}),
          })
        : undefined;

    const payload = create(AgentSchema, {
      name: form.name,
      description: form.description,
      modelConfig,
      advancedConfig,
      channelConfigs,
      pluginConfigs,
    });

    createMutation.mutate({
      ...payload,
      interleavedThinking: extraConfig.interleavedThinking,
      toolPreHooks: buildToolPreHooks(
        form.toolPermissions.preHooks,
        form.toolPermissions.aiApproval
      ),
    });
  };

  const handlers: AgentFormHandlers = {
    addPlugin,
    removePlugin,
    updatePlugin,
    updatePluginParam,
    fetchPluginInfo,
    addChannel,
    removeChannel,
    updateChannel,
    getDuplicateParamNames,
  };

  return (
    <AgentForm
      form={form}
      set={set}
      handlers={handlers}
      showApiKey={showApiKey}
      setShowApiKey={setShowApiKey}
      credentialPicker
      restrictTemplateFields
      title="创建智能体模版"
      subtitle="填写以下信息创建一个新的智能代理"
      submitLabel="创建智能体模版"
      isPending={createMutation.isPending}
      backTo="/agents"
      canSubmit={
        !!form.name.trim() &&
        !!form.api_key_ref.trim() &&
        !!form.model_name.trim() &&
        (!form.webSearchEnable || !!form.webSearchApiKey.trim())
      }
      onSubmit={handleSubmit}
    />
  );
}
